// src/services/projectUserApi.ts
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
import { createAuthHeaders } from '../utils/apiHelpers';

// Project User Types
// Note: Backend may return either nested user object OR flat properties (userEmail, userName, etc.)
export interface ProjectUser {
  projectUserId: number;
  projectId: number;
  userId: number;
  // Nested user object (preferred structure)
  user?: {
    userId: number;
    email: string;
    displayName: string | null;
    company: string | null;
    userType: string | null;
    role: string | null;
    isActive: boolean;
  };
  // Flat properties (alternative structure from backend)
  userEmail?: string;
  userName?: string;
  userCompany?: string | null;
  userUserType?: string | null;
  userRole?: string | null;
  // ProjectUser-specific fields
  userType: string | null; // TENANT | FRAPORT | CLIENT
  company: string | null;
  assignedAt: string; // ISO 8601 datetime
  assignedByUserId: number | null;
}

export interface ProjectUsersResponse {
  users: ProjectUser[];
  total: number;
}

export interface AddUserToProjectRequest {
  userId: number;
  userType?: string | null;
  company?: string | null;
}

export interface InviteUserToProjectRequest {
  email: string;
  name?: string | null; // Required for new users; optional for existing users
  userType?: string | null; // TENANT | FRAPORT | CLIENT
  company?: string | null;
  userAccessToken?: string | null; // Optional user access token for delegated authentication
}

export interface InviteUserToProjectResponse {
  success: boolean;
  message: string;
  user?: ProjectUser;
  invitationId?: number;
}

export interface IncompleteAction {
  actionId: number;
  actionType: string;
  taskName: string;
  phaseName: string;
}

export interface IncompleteActionsResponse {
  actions: IncompleteAction[];
  total: number;
  projectStatus?: string | null;
  canRemove?: boolean;
  willUnassign?: boolean;
}

// Error Type
export interface ProjectUserApiError {
  error: boolean;
  message: string;
  code?: 'AUTH_REQUIRED' | 'NOT_FOUND' | 'VALIDATION_ERROR' | 'NETWORK_ERROR' | 'CONFLICT';
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

/**
 * Get all users in a project
 */
export async function getProjectUsers(projectId: number): Promise<ProjectUsersResponse> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/users`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectUserApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectUserApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to get project users: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectUserApiError;
  }

  const data = await response.json();
  return data;
}

/**
 * Add an existing user to a project
 */
export async function addUserToProject(
  projectId: number,
  data: AddUserToProjectRequest
): Promise<ProjectUser> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/users`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectUserApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as ProjectUserApiError;
  }

  if (response.status === 409) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'User is already in this project',
      code: 'CONFLICT',
    } as ProjectUserApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project or user not found', code: 'NOT_FOUND' } as ProjectUserApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to add user to project: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectUserApiError;
  }

  return response.json();
}

/**
 * Invite a user to a project (creates invitation if user doesn't exist)
 */
export async function inviteUserToProject(
  projectId: number,
  data: InviteUserToProjectRequest
): Promise<InviteUserToProjectResponse> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/users/invite`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectUserApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as ProjectUserApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectUserApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to invite user to project: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectUserApiError;
  }

  return response.json();
}

/**
 * Remove a user from a project
 */
export async function removeUserFromProject(projectId: number, userId: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/users/${userId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectUserApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Cannot remove user with incomplete actions',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as ProjectUserApiError;
  }

  if (response.status === 404) {
    let message = 'Project or user not found';
    try {
      const error = await response.json();
      message = error.message || message;
    } catch {
      // keep default
    }
    throw { error: true, message, code: 'NOT_FOUND' } as ProjectUserApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to remove user from project: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectUserApiError;
  }
}

/**
 * Get incomplete actions for a user in a project
 */
export async function getIncompleteActions(
  projectId: number,
  userId: number
): Promise<IncompleteActionsResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/projects/${projectId}/users/${userId}/incomplete-actions`,
    {
      method: 'GET',
      credentials: 'include',
      headers: createAuthHeaders(),
    }
  );

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectUserApiError;
  }

  if (response.status === 404) {
    let message = 'Project or user not found';
    try {
      const error = await response.json();
      message = error.message || message;
    } catch {
      // keep default
    }
    throw { error: true, message, code: 'NOT_FOUND' } as ProjectUserApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to get incomplete actions: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectUserApiError;
  }

  return response.json();
}

